import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SKIN_TONES, HAIR_COLORS, OUTFIT_COLORS, HAIR_STYLES, OUTFITS, ACCESSORIES, PETS, LOOKS,
  DEFAULT_AVATAR, randomAvatar, getPet,
} from "@/data/avatarData";
import Avatar from "./Avatar";
import Mascot from "./Mascot";
import { Sound } from "@/lib/sound";
import { Shuffle, Check } from "lucide-react";

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">{title}</div>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

const Swatch = ({ color, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{ backgroundColor: color }}
    className={`w-8 h-8 rounded-full border-4 transition-transform hover:scale-110 ${
      active ? "border-slate-900 scale-110" : "border-white shadow"
    }`}
    aria-label={color}
  />
);

const Chip = ({ label, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-xl border-2 text-xs font-heading font-black transition-colors ${
      active ? "bg-amber-400 border-amber-600 text-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
    }`}
  >
    {label}
  </button>
);

const AvatarEditor = ({ initial, onSave, saveLabel = "Save my look" }) => {
  const [a, setA] = useState({ ...DEFAULT_AVATAR, ...(initial || {}) });
  const set = (patch) => { Sound.tap(); setA((x) => ({ ...x, ...patch })); };
  const pet = getPet(a.pet);

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-5" data-testid="avatar-editor">
      <div className="bg-amber-50 border-4 border-amber-200 rounded-3xl p-4 flex flex-col items-center justify-center gap-2">
        <Avatar avatar={a} size={150} testid="avatar-preview" />
        <div className="mt-2">
          <Mascot pet={a.pet} size={72} name={a.petName} testid="mascot-preview" />
        </div>
        <Button
          type="button"
          onClick={() => { Sound.whoosh(); setA(randomAvatar()); }}
          data-testid="avatar-random-btn"
          className="mt-1 bg-white hover:bg-amber-100 text-amber-700 font-heading font-black rounded-xl border-2 border-amber-300 btn-3d text-xs px-3 py-2 h-auto"
        >
          <Shuffle className="w-3.5 h-3.5 mr-1" /> Surprise me!
        </Button>
      </div>

      <div className="space-y-3">
        <Section title="I am a…">
          {LOOKS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => set({ look: l.id, hairStyle: l.id === "boy" ? "short" : "long", outfit: l.id === "girl" ? "dress" : a.outfit === "dress" ? "tee" : a.outfit })}
              data-testid={`look-${l.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-2xl border-2 font-heading font-black text-sm transition-transform hover:scale-105 ${
                a.look === l.id ? "bg-amber-400 border-amber-600 text-slate-900" : "bg-white border-slate-200 text-slate-600"
              }`}
            >
              <span className="text-xl">{l.emoji}</span> {l.name}
            </button>
          ))}
        </Section>
        <Section title="Skin">
          {SKIN_TONES.map((c, i) => (
            <Swatch key={c} color={c} active={a.skin === i} onClick={() => set({ skin: i })} testid={`skin-${i}`} />
          ))}
        </Section>
        <Section title="Hair style">
          {HAIR_STYLES.map((h) => (
            <Chip key={h.id} label={h.name} active={a.hairStyle === h.id} onClick={() => set({ hairStyle: h.id })} testid={`hair-${h.id}`} />
          ))}
        </Section>
        <Section title="Hair color">
          {HAIR_COLORS.map((c, i) => (
            <Swatch key={c} color={c} active={a.hairColor === i} onClick={() => set({ hairColor: i })} testid={`haircolor-${i}`} />
          ))}
        </Section>
        <Section title="Outfit">
          {OUTFITS.map((o) => (
            <Chip key={o.id} label={o.name} active={a.outfit === o.id} onClick={() => set({ outfit: o.id })} testid={`outfit-${o.id}`} />
          ))}
        </Section>
        <Section title="Outfit color">
          {OUTFIT_COLORS.map((c, i) => (
            <Swatch key={c} color={c} active={a.outfitColor === i} onClick={() => set({ outfitColor: i })} testid={`outfitcolor-${i}`} />
          ))}
        </Section>
        <Section title="Accessory">
          {ACCESSORIES.map((x) => (
            <Chip key={x.id} label={x.name} active={a.accessory === x.id} onClick={() => set({ accessory: x.id })} testid={`accessory-${x.id}`} />
          ))}
        </Section>
        <Section title="Mascot pet">
          {PETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => set({ pet: p.id, petName: a.petName === pet.defaultName ? p.defaultName : a.petName })}
              data-testid={`pet-${p.id}`}
              className={`rounded-2xl border-2 p-1 transition-transform hover:scale-105 ${
                a.pet === p.id ? "bg-amber-100 border-amber-500" : "bg-white border-slate-200"
              }`}
            >
              <Mascot pet={p.id} size={44} testid={`pet-icon-${p.id}`} />
              <div className="text-[10px] font-black text-slate-700">{p.name}</div>
            </button>
          ))}
        </Section>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Pet name</div>
          <Input
            value={a.petName}
            maxLength={14}
            onChange={(e) => setA((x) => ({ ...x, petName: e.target.value }))}
            data-testid="pet-name-input"
            className="rounded-xl border-2 border-slate-300 font-bold max-w-xs"
          />
        </div>
        <Button
          type="button"
          onClick={() => { Sound.success(); onSave({ ...a, petName: a.petName.trim() || pet.defaultName }); }}
          data-testid="avatar-save-btn"
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black font-heading text-base py-6 rounded-2xl btn-3d border-2 border-emerald-600"
        >
          <Check className="w-5 h-5 mr-1" /> {saveLabel}
        </Button>
      </div>
    </div>
  );
};

export default AvatarEditor;
